"use client";

import React, { useState, useEffect } from "react";
import { Globe, Clock, CircleCheck, Loader, ShieldCheck, Activity } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
const useWorldTime = () => {
  const [times, setTimes] = useState({
    NY: "",
    LDN: "",
    SYD: ""
  });
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTimes({
        NY: now.toLocaleTimeString("en-US", {
          timeZone: "America/New_York",
          hour: "2-digit",
          minute: "2-digit"
        }),
        LDN: now.toLocaleTimeString("en-GB", {
          timeZone: "Europe/London",
          hour: "2-digit",
          minute: "2-digit"
        }),
        SYD: now.toLocaleTimeString("en-AU", {
          timeZone: "Australia/Sydney",
          hour: "2-digit",
          minute: "2-digit"
        })
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);
  return times;
};
function StatusIndicator() {
  const times = useWorldTime();
  const regions = [{
    code: "GMT-5",
    city: "New York",
    time: times.NY,
    status: "Active"
  }, {
    code: "GMT+0",
    city: "London",
    time: times.LDN,
    status: "Active"
  }, {
    code: "GMT+11",
    city: "Sydney",
    time: times.SYD,
    status: "Standby"
  }];
  return;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex min-h-[400px] flex-col items-center justify-center space-y-6 rounded-none border border-primary/10 bg-white p-8 text-center animate-in fade-in zoom-in duration-500 shadow-sm">
        <div className="flex h-16 w-16 items-center justify-center rounded-none bg-primary text-primary-foreground ring-1 ring-primary/20">
          <CircleCheck className="h-8 w-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif font-bold tracking-tight text-primary">
            Request Authenticated
          </h3>
          <p className="max-w-xs text-secondary-foreground mx-auto text-sm">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/20 rounded-none hover:bg-primary hover:text-white transition-colors uppercase text-xs tracking-widest">
          New Partnership Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Principal Name
              </FormLabel>
              <FormControl>
                <Input placeholder="LEVENT HAMDEMIR" {...field} className="border-0 border-b border-primary/20 bg-transparent px-0 py-2 text-lg font-medium shadow-none transition-all placeholder:text-muted-foreground/20 focus-visible:border-primary focus-visible:ring-0 rounded-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Official Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="PARTNER@CORPORATION.COM" {...field} className="border-0 border-b border-primary/20 bg-transparent px-0 py-2 text-lg font-medium shadow-none transition-all placeholder:text-muted-foreground/20 focus-visible:border-primary focus-visible:ring-0 rounded-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="company" rules={{
        required: "Company is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Organization
              </FormLabel>
              <FormControl>
                <Input placeholder="GLOBAL ENTERPRISE" {...field} className="border-0 border-b border-primary/20 bg-transparent px-0 py-2 text-lg font-medium shadow-none transition-all placeholder:text-muted-foreground/20 focus-visible:border-primary focus-visible:ring-0 rounded-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="role" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Designation <span className="opacity-50">(Optional)</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="CHIEF STRATEGY OFFICER" {...field} className="border-0 border-b border-primary/20 bg-transparent px-0 py-2 text-lg font-medium shadow-none transition-all placeholder:text-muted-foreground/20 focus-visible:border-primary focus-visible:ring-0 rounded-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Direct Line
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 7946 0000" {...field} className="border-0 border-b border-primary/20 bg-transparent px-0 py-2 text-lg font-medium shadow-none transition-all placeholder:text-muted-foreground/20 focus-visible:border-primary focus-visible:ring-0 rounded-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" rules={{
        required: "Preferred date is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Proposed Briefing Date
              </FormLabel>
              <FormControl>
                <Input type="date" {...field} className="border-0 border-b border-primary/20 bg-transparent px-0 py-2 text-lg font-medium shadow-none transition-all placeholder:text-muted-foreground/20 focus-visible:border-primary focus-visible:ring-0 rounded-none [color-scheme:light]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Strategic Objectives <span className="opacity-50">(Optional)</span>
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Outline your expansion or strategy requirements..." {...field} className="min-h-[100px] resize-none border-0 border-b border-primary/20 bg-transparent px-0 py-2 text-lg font-medium shadow-none transition-all placeholder:text-muted-foreground/20 focus-visible:border-primary focus-visible:ring-0 rounded-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-none border border-destructive/30 bg-destructive/5 p-4 text-xs text-destructive uppercase tracking-widest">
          <p className="font-bold">Transmission Error</p>
          <p className="mt-1 opacity-80">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="group relative h-14 w-full overflow-hidden rounded-none bg-primary text-primary-foreground text-sm font-bold uppercase tracking-[0.3em] hover:bg-primary/95 transition-all">
          <span className="relative z-10 flex items-center gap-2">
            {isSubmitting ? <>
                <Loader className="h-4 w-4 animate-spin" />
                Processing Protocol...
              </> : <>
                Submit Formal Inquiry
                <Globe className="h-3 w-3 transition-transform group-hover:rotate-12 opacity-70" />
              </>}
          </span>
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_5s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </Button>
        <p className="mt-4 text-center text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
          Limited availability for new partnerships in Q4 2024.
        </p>
      </div>
    </Form>;
}
export default function DirectConsultation() {
  return <section id="strategic-partnership-inquiry" className="bg-secondary text-foreground relative min-h-screen w-full overflow-hidden py-24 md:py-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,var(--primary)_0%,transparent_70%)] opacity-[0.02]" />
      <div className="pointer-events-none absolute top-0 left-1/2 h-px w-full -translate-x-1/2 bg-gradient-to-r from-transparent via-primary/10 to-transparent opacity-30" />

      <div className="container relative mx-auto px-4">
        <div className="relative mx-auto max-w-[700px]">
          <div className="mb-16 text-center">
            <Badge variant="outline" className="mb-6 border-primary/20 text-primary uppercase tracking-[0.3em] rounded-none bg-white/50 px-4 py-1">
              Limited Engagement
            </Badge>
            <h2 className="font-serif text-4xl font-bold tracking-tight text-primary md:text-5xl lg:text-6xl">Let's Explore the Opportunity</h2>
            <p className="mt-6 text-base text-secondary-foreground uppercase tracking-[0.1em] max-w-md mx-auto">Tell us where you are today, where you want to go, and what challenges you're facing in the U.S. market.</p>
          </div>

          <StatusIndicator />

          <div className="relative z-10 bg-white/40 backdrop-blur-[2px] p-1 md:p-0">
            <FormProvider formId="consultation_form_tcc_strategy" sectionName="strategic-partnership-inquiry" defaultValues={{
            full_name: "",
            email: "",
            company: "",
            role: "",
            phone: "",
            preferred_date: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            company: {
              required: true
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            preferred_date: {
              required: true
            }
          }} submitText="Submit Formal Inquiry" successMessage="Your inquiry has been logged. Our partners will review and respond within 24 business hours." errorMessage="Transmission failed. Please verify your connection and try again.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          10% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        section#strategic-partnership-inquiry {
          background-image: url("https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582219/img/natural-paper.png");
          background-blend-mode: multiply;
        }
      `}</style>
    </section>;
}